import React from 'react';
import { AlertTriangle, RefreshCw, X } from 'lucide-react';

interface ErrorBannerProps {
  error: unknown;
  title?: string;
  onRetry?: () => void;
  onDismiss?: () => void;
}

/**
 * Displays an error banner for query failures.
 * Shows user-friendly error message with optional retry button.
 * 
 * Design: Matches Ministry of Wine design system
 * - Background: white with border
 * - Border: error-500 (#EA4335 red)
 * - Icon: error-500
 * - Text: neutral-900 (dark)
 */
export function ErrorBanner({ error, title = 'Error Loading Data', onRetry, onDismiss }: ErrorBannerProps) {
  const getMessage = (err: unknown): string => {
    if (err instanceof Error) {
      // Network errors
      if (err.message.includes('Network Error') || err.message.includes('ERR_CONNECTION_REFUSED')) {
        return 'Unable to connect to the server. Please check if the API is running.';
      }
      return err.message;
    }
    
    if (typeof err === 'object' && err !== null) {
      if ('message' in err && typeof err.message === 'string') {
        return err.message;
      }
      if ('error' in err && typeof err.error === 'object' && err.error !== null) {
        if ('message' in err.error && typeof (err.error as any).message === 'string') {
          return (err.error as any).message;
        }
      }
    }
    
    return 'An unexpected error occurred. Please try again.';
  };

  const message = getMessage(error);

  return (
    <div className="bg-white border-2 border-error-500 rounded-lg p-6 shadow-lg animate-in slide-in-from-top-4 duration-300">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          <AlertTriangle className="h-6 w-6 text-error-500" strokeWidth={2} aria-hidden="true" />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-semibold text-neutral-900 mb-1">{title}</h3>
          <p className="text-sm text-neutral-700">{message}</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-error-500 hover:text-error-600 border border-error-500 hover:border-error-600 rounded-md transition-colors"
            >
              <RefreshCw className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
              Try again
            </button>
          )}
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="flex-shrink-0 text-neutral-400 hover:text-neutral-600 transition-colors"
            aria-label="Dismiss error"
          >
            <X className="h-5 w-5" strokeWidth={2} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}
